import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../../state/store";
import { fetchData } from "../../../helpers/offthreadFetcher";
import { buildCompareDateRange, formatComparisonApiUrl, parseDate } from "../utils";
import { useComparisonState } from "./useComparisonState";
import type { ComparisonApiResponse } from "../types";

export const useComparisonData = () => {
  const comparison = useComparisonState();
  const routeMatch = useParams();
  const {
    dates: dateFilters,
    timeRange: timeFilters,
    widgets: widgetFilters,
    locations: locationFilters,
  } = useSelector((state: RootState) => state.filters);
  const { company } = useSelector((state: RootState) => state);
  const timezoneOffset = company?.company_details?.time_zone_offset || "";

  const [data, setData] = useState<ComparisonApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const fetchIdRef = useRef(0);

  const fetchComparison = useCallback(async () => {
    if (!comparison.isActive || !comparison.cmp_mode || !comparison.cmp_option) {
      setData(null);
      return;
    }

    const [startDate, endDate] = dateFilters;
    if (!startDate || !endDate) return;

    const baseDateRange = {
      startDate: parseDate(startDate),
      endDate: parseDate(endDate),
    };

    const compareDateRange = buildCompareDateRange({
      baseDateRange,
      mode: comparison.cmp_mode,
      option: comparison.cmp_option,
      count: comparison.cmp_count ?? undefined,
      subRange: comparison.cmp_sub ?? undefined,
      customFrom: comparison.cmp_from ?? undefined,
      customTo: comparison.cmp_to ?? undefined,
    });

    if (!compareDateRange[0] || !compareDateRange[1]) return;

    const baseUrl = `/api/v1/dar/${routeMatch?.dashboard}/${routeMatch?.widget}/comparison`;

    const url = formatComparisonApiUrl({
      baseUrl,
      mode: comparison.cmp_mode,
      comparisonDateRange: compareDateRange,
      dateRange: dateFilters as [string, string],
      timeRange: timeFilters as [[number, number], [number, number]],
      timezoneOffset,
      widgetFilters: widgetFilters || [],
      locationFilters: locationFilters || [],
      dimension: comparison.cmp_dim,
    });

    const currentFetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchData(url);
      if (fetchIdRef.current === currentFetchId) {
        setData(result);
        setIsLoading(false);
      }
    } catch (err: any) {
      if (fetchIdRef.current === currentFetchId) {
        setError(err);
        setIsLoading(false);
      }
    }
  }, [
    comparison.isActive,
    comparison.cmp_mode,
    comparison.cmp_option,
    comparison.cmp_count,
    comparison.cmp_sub,
    comparison.cmp_from,
    comparison.cmp_to,
    comparison.cmp_dim,
    JSON.stringify(dateFilters),
    JSON.stringify(timeFilters),
    JSON.stringify(widgetFilters),
    JSON.stringify(locationFilters),
    timezoneOffset,
    routeMatch?.dashboard,
    routeMatch?.widget,
  ]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  useEffect(() => {
    if (!comparison.isActive) {
      setData(null);
      setError(null);
      setIsLoading(false);
    }
  }, [comparison.isActive]);

  return { data, isLoading, error, refetch: fetchComparison };
};
